import { named, optional, withDependencies } from '@wix/thunderbolt-ioc'
import {
	IPageWillMountHandler,
	IPageDidUnmountHandler,
	IPropsStore,
	PageFeatureConfigSymbol,
	SiteFeatureConfigSymbol,
	Props,
	PropsMap,
	IMultilingual,
} from '@wix/thunderbolt-symbols'
import { IUrlHistoryManager, UrlHistoryManagerSymbol } from 'feature-router'
import { ISiteScrollBlocker, SiteScrollBlockerSymbol } from 'feature-site-scroll-blocker'
import { SiteMembersApiSymbol, ISiteMembersApi } from 'feature-site-members'
import { MultilingualSymbol } from 'feature-multilingual'
import { name } from './symbols'
import type { TinyMenuPageConfig, TinyMenuSiteConfig } from './types'

export const resolveMemberDetails = async (siteMembersApi: ISiteMembersApi) => {
	let membersData = {
		isLoggedIn: false,
		userName: '',
		avatarUri: '',
	}
	try {
		const memberDetails = await siteMembersApi.getMemberDetails()
		if (memberDetails) {
			membersData = {
				isLoggedIn: true,
				userName: memberDetails.nickname || memberDetails.memberName || memberDetails.loginEmail,
				avatarUri: memberDetails.imageUrl,
			}
		}
	} catch (e) {
		console.error('failed to fetch member details', e)
	}
	return membersData
}

const tinyMenuFactory = (
	{ componentId, hasClassicSkin, isFixed, hasMembersArea }: TinyMenuPageConfig,
	propsStore: IPropsStore,
	urlHistoryManager: IUrlHistoryManager,
	siteScrollBlocker: ISiteScrollBlocker,
	siteConfig: TinyMenuSiteConfig,
	siteMembersApi?: ISiteMembersApi,
	multilingual?: IMultilingual
): IPageWillMountHandler & IPageDidUnmountHandler => {
	let onLoginCallbackId: unknown
	let onMemberDetailsRefreshCallbackId: unknown

	const updateMembersProps = async () => {
		const memberDetails = await resolveMemberDetails(siteMembersApi!)
		const updatePropsMap: PropsMap = {
			[componentId]: {
				...memberDetails,
				onLogin() {
					siteMembersApi!.promptLogin()
				},
				onLogout() {
					siteMembersApi!.logout()
				},
			},
		}

		propsStore.update(updatePropsMap)
	}

	return {
		name: 'tinyMenu',
		async pageWillMount() {
			const currentPrimaryPageHref = urlHistoryManager.getFullUrlWithoutQueryParams()

			const scrollBlockedListener = {
				handleBlockedBy(blockedCompId: string) {
					if (blockedCompId !== componentId) {
						propsStore.update({ [componentId]: { isHidden: true } })
					}
				},
				handleUnblockedBy(unblockedCompId: string) {
					if (unblockedCompId !== componentId) {
						propsStore.update({ [componentId]: { isHidden: false } })
					}
				},
			}

			const updatedProps = {
				[componentId]: {
					currentPrimaryPageHref,
					...((!hasClassicSkin || isFixed) && {
						onMenuOpen: () => siteScrollBlocker.setSiteScrollingBlocked(true, componentId),
						onMenuClose: () => siteScrollBlocker.setSiteScrollingBlocked(false, componentId),
					}),
					onLanguageChange: multilingual && multilingual.setCurrentLanguage,
					languages: siteConfig.languages,
				},
			}

			siteScrollBlocker.registerScrollBlockedListener(scrollBlockedListener)
			propsStore.update(updatedProps)

			if (siteMembersApi && hasMembersArea) {
				onLoginCallbackId = siteMembersApi.registerToUserLogin(updateMembersProps)
				onMemberDetailsRefreshCallbackId = siteMembersApi.registerToMemberDetailsRefresh(updateMembersProps)
				await updateMembersProps()
			}
		},
		async pageDidUnmount() {
			if (siteMembersApi && hasMembersArea) {
				siteMembersApi.unRegisterToUserLogin(onLoginCallbackId)
				siteMembersApi.unRegisterToMemberDetailsRefresh(onMemberDetailsRefreshCallbackId)
			}
		},
	}
}

export const TinyMenu = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		Props,
		UrlHistoryManagerSymbol,
		SiteScrollBlockerSymbol,
		named(SiteFeatureConfigSymbol, name),
		optional(SiteMembersApiSymbol),
		optional(MultilingualSymbol),
	],
	tinyMenuFactory
)
